import _extends from "@babel/runtime/helpers/extends";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { useIntl } from 'react-intl-next';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { useAgentRefinementTagProps } from '../../hooks/use-agent-refinement-tag-props';
import { BackIconButton } from '../BackIconButton/BackIconButton';
import { EditPromptHoverTag } from '../EditPromptHoverTag/EditPromptHoverTag';
import { LatestPromptRenderer } from '../LatestPromptRenderer/LatestPromptRenderer';
import { RefinementTag } from '../RefinementTag/RefinementTag';
import { messages } from './messages';
var PROMPT_CONTENT_ID = 'ai-latest-prompt-content';
var headerEditStyles = css({
  cursor: 'pointer',
  border: 'none',
  width: '100%',
  backgroundColor: 'transparent',
  color: 'inherit',
  textAlign: 'start',
  fontSize: 'unset',
  lineHeight: 'inherit',
  borderRadius: "var(--ds-border-radius-200, 4px)",
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  '&:hover': {
    backgroundColor: "var(--ds-background-neutral-subtle-hovered, #091E420F)"
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&:hover #edit-prompt-hover-button': {
    opacity: 1
  },
  '&:active': {
    backgroundColor: "var(--ds-background-neutral-subtle-pressed, #091E4224)"
  },
  '&:focus': {
    outline: "2px solid ".concat("var(--ds-border-focused, #4C9AFF)"),
    outlineOffset: "var(--ds-space-negative-025, -2px)"
  }
});
var contentContainerStyles = css({
  lineHeight: '20px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  hyphens: 'auto',
  wordWrap: 'break-word'
});
var fullWidth = xcss({
  width: '100%'
});
var titleContentWrapper = xcss({
  flexGrow: 1,
  // https://css-tricks.com/flexbox-truncated-text/
  minWidth: '0'
});
var wrapText = xcss({
  whiteSpace: 'normal'
});
var headerStyles = css({
  padding: "var(--ds-space-150, 12px)",
  overflowWrap: 'break-word',
  overflow: 'hidden',
  width: '100%',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  'div p': {
    lineHeight: '20px'
  }
});

// header has width/padding at the same time
// if there is very long prompt, header will be wider than parent
// so it should be border-box
var headerBoxSizingStyles = css({
  boxSizing: 'border-box'
});
export var LatestPromptHeader = function LatestPromptHeader(_ref) {
  var parentPresetPromptLabel = _ref.parentPresetPromptLabel,
    childShortTitle = _ref.childShortTitle,
    prompt = _ref.prompt,
    promptADF = _ref.promptADF,
    presetTitle = _ref.presetTitle,
    providerFactory = _ref.providerFactory,
    onEditPromptClick = _ref.onEditPromptClick,
    agent = _ref.agent,
    showBack = _ref.showBack,
    onBack = _ref.onBack;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;

  // when used in Loading screen, there is no onEditPromptClick
  var canEdit = !!onEditPromptClick;
  if (agent && !editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
    canEdit = false;
  }
  var promptContent = React.useMemo(function () {
    if (editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
      if (promptADF) {
        return jsx(Flex, {
          alignItems: "center"
        }, jsx(LatestPromptRenderer, {
          promptADF: promptADF,
          providerFactory: providerFactory
        }), !presetTitle && jsx(EditPromptHoverTag, {
          isClearMode: !presetTitle && !prompt,
          isPlaceholder: !canEdit
        }));
      }
      if (prompt) {
        return jsx("div", {
          css: contentContainerStyles
        }, prompt);
      }
      return null;
    }
    if (!editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
      if (promptADF) {
        return jsx(LatestPromptRenderer, {
          promptADF: promptADF,
          providerFactory: providerFactory
        });
      }
      return jsx(Box, {
        xcss: wrapText
      }, prompt);
    }
  }, [canEdit, prompt, promptADF, providerFactory, presetTitle]);
  var refinementTagProps = useAgentRefinementTagProps(agent);
  var header = React.useMemo(function () {
    if (editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
      return jsx(Flex, null, showBack && jsx(BackIconButton, {
        inPromptHeader: true,
        isDisabled: !canEdit,
        stopPropagation: true,
        onClick: onBack
      }), jsx(Flex, {
        direction: "column",
        gap: "space.050",
        xcss: titleContentWrapper
      }, presetTitle && jsx(Flex, {
        alignItems: "center"
      }, parentPresetPromptLabel && childShortTitle && !editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? jsx(Flex, {
        alignItems: "center",
        gap: "space.050"
      }, jsx(RefinementTag, _extends({
        label: parentPresetPromptLabel
      }, refinementTagProps)), jsx(Text, {
        weight: "bold",
        testId: "child-short-title"
      }, childShortTitle)) : jsx("div", {
        onClick: function onClick(e) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, jsx(RefinementTag, _extends({
        label: presetTitle
      }, refinementTagProps, {
        onClick: editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? function () {
          if (onBack) {
            onBack();
          }
        } : undefined
      }))), presetTitle && jsx(EditPromptHoverTag, {
        isClearMode: !!presetTitle && !prompt,
        isPlaceholder: !canEdit
      })), promptContent));
    }
    return jsx(React.Fragment, null, presetTitle && jsx(RefinementTag, _extends({
      label: presetTitle
    }, refinementTagProps)), presetTitle && (prompt || promptADF) && jsx(Box, {
      paddingBlockStart: "space.050"
    }), promptContent);
  }, [presetTitle, refinementTagProps, prompt, promptADF, promptContent, showBack, canEdit, onBack, parentPresetPromptLabel, childShortTitle]);
  if (!(prompt || presetTitle)) {
    return null;
  }
  return jsx(Box, {
    testId: "prompt-header",
    xcss: fullWidth
  }, !canEdit ? jsx("div", {
    css: [headerStyles, editorExperiment('platform_editor_ai_command_palate_improvement', 'test') && headerBoxSizingStyles, contentContainerStyles],
    id: PROMPT_CONTENT_ID,
    "data-testid": PROMPT_CONTENT_ID
  }, header) :
  // eslint-disable-next-line @atlaskit/design-system/no-html-button
  jsx("button", {
    type: "button",
    "data-testid": "edit-prompt-button",
    onClick: onEditPromptClick,
    css: [headerStyles, editorExperiment('platform_editor_ai_command_palate_improvement', 'test') && headerBoxSizingStyles, contentContainerStyles, headerEditStyles],
    "aria-label": formatMessage(messages.editPrompt),
    "aria-describedby": PROMPT_CONTENT_ID
  }, jsx("div", {
    id: PROMPT_CONTENT_ID,
    "data-testid": PROMPT_CONTENT_ID
  }, header)));
};